import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import useColumnConfig, { getStoredPageSize } from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Segmented,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CloudDownloadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getIntakes, getIntake, getIntakeRefusals, resendIntake } from '../modules/partner-intake/api';
import './ApiIntake.scss';

const { Paragraph } = Typography;

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm:ss') : '—');
const fmtDate = (iso) => (iso ? dayjs(iso).format('YYYY-MM-DD') : '—');

const VERDICT_COLOR = { CREATED: 'green', SKIPPED: 'orange', REJECTED: 'red', OVO_API: 'volcano' };

// "Order already exists" (ovo-api's Airtable dedup) or the planner's
// "… already exists (CODE)" — a partner retry, harmless only when the
// planner really has the order.
const isDuplicateAnswer = (text) => /already exist/i.test(String(text ?? ''));

/**
 * Per-request chips (OVO-292, severity split Sep 2026). A serious error is
 * an order the planner failed on or refused — or an "already exists" for an
 * order the planner does NOT have. "Already there" retries are grey: they
 * used to be counted as errors and buried the orders that were really lost.
 */
const OutcomeSummary = ({ summary, t }) => {
  if (!summary) return '—';
  const { created = 0, duplicates = 0, errors = 0, missing = 0, skipped = 0, rejected = 0 } = summary;
  const resolved = Math.max(errors - missing, 0);
  if (!created && !duplicates && !errors && !skipped && !rejected) {
    return <span style={{ color: 'var(--text-muted)' }}>{t('apiIntake.noVerdict')}</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {missing > 0 && (
        <Tooltip title={t('apiIntake.summaryMissingHint')}>
          <Tag color="red" icon={<ExclamationCircleOutlined />} data-testid="intake-chip-missing">
            {t('apiIntake.summaryMissing', { count: missing })}
          </Tag>
        </Tooltip>
      )}
      {resolved > 0 && (
        <Tooltip title={t('apiIntake.summaryResolvedHint')}>
          <Tag color="orange" data-testid="intake-chip-resolved">
            {t('apiIntake.summaryResolved', { count: resolved })}
          </Tag>
        </Tooltip>
      )}
      {created > 0 && <Tag color="green">{t('apiIntake.summaryCreated', { count: created })}</Tag>}
      {duplicates > 0 && (
        <Tooltip title={t('apiIntake.summaryDuplicatesHint')}>
          <Tag data-testid="intake-chip-duplicates">{t('apiIntake.summaryDuplicates', { count: duplicates })}</Tag>
        </Tooltip>
      )}
    </span>
  );
};

const intakeRowClass = (rec) =>
  rec.outcomeSummary?.missing > 0 ? 'intake-row-missing' : rec.outcomeSummary?.errors > 0 ? 'intake-row-error' : '';

// A partner order id is NOT unique across partners (BEAN and ET both have
// 39142): every trace link from here carries the partner it came from, so the
// trace never shows another partner's order with the same id.
const traceUrl = (id, partnerCode) =>
  `/admin/diagnostics?mode=trace&q=${encodeURIComponent(id)}${
    partnerCode ? `&partner=${encodeURIComponent(partnerCode)}` : ''
  }`;

/**
 * OVO-292 — the per-order view of ONE intake: every partner_order_id in the
 * raw body × ovo-api's answer × the planner's verdict, with the order link
 * when it was created. The expanded row used to show only the raw JSON.
 */
const IntakeOrdersTable = ({ detail, partnerCode, t }) => {
  const items = Array.isArray(detail?.body?.orders) ? detail.body.orders : [];
  const results = Array.isArray(detail?.results) ? detail.results : [];
  const outcomes = Array.isArray(detail?.outcomes) ? detail.outcomes : [];
  // Sep 2026 — does each order exist in the planner NOW (live / trash)?
  const presence = detail?.presence || {};
  const resultByRef = new Map(results.map((r) => [String(r?.partner_order_id ?? ''), r]));
  const outcomeByRef = new Map();
  for (const o of outcomes)
    if (o.partnerOrderRef && !outcomeByRef.has(o.partnerOrderRef)) outcomeByRef.set(o.partnerOrderRef, o);
  const rows = items.map((it, i) => {
    const ref = String(it?.partner_order_id ?? '');
    return {
      key: `${ref}-${i}`,
      ref,
      name: it?.destination?.name || it?.destination?.company || '—',
      zip: it?.destination?.zip || '—',
      date: it?.planned_date ? String(it.planned_date).slice(0, 10) : '—',
      result: resultByRef.get(ref) || null,
      outcome: outcomeByRef.get(ref) || null,
      presence: presence[ref] || null,
    };
  });
  // Grey: a retry for an order the planner has. Red: an error, or an
  // "already exists" for an order that is NOT in the planner. Orange: an
  // error whose order exists now (resolved).
  const answerTag = (r) => {
    const { error } = r.result;
    if (!r.presence) return <Tag color="red">{error}</Tag>;
    if (isDuplicateAnswer(error) || isDuplicateAnswer(r.outcome?.reason)) {
      return (
        <Tooltip title={error}>
          <Tag>{t('apiIntake.answerDuplicate')}</Tag>
        </Tooltip>
      );
    }
    return <Tag color="orange">{error}</Tag>;
  };
  const columns = [
    {
      title: t('apiIntake.colPartnerRef'),
      dataIndex: 'ref',
      key: 'ref',
      render: (v) => (
        <Link to={traceUrl(v, partnerCode || detail?.partnerCode)} title={t('apiIntake.trace')}>
          {v || '—'}
        </Link>
      ),
    },
    { title: t('apiIntake.colName'), dataIndex: 'name', key: 'name' },
    { title: t('apiIntake.colZip'), dataIndex: 'zip', key: 'zip', width: 70 },
    { title: t('apiIntake.colDate'), dataIndex: 'date', key: 'date', width: 110 },
    {
      title: t('apiIntake.colOvoApi'),
      key: 'result',
      render: (_, r) =>
        !detail?.results ? (
          <span style={{ color: 'var(--text-muted)' }}>{t('apiIntake.ovoApiUnknown')}</span>
        ) : !r.result ? (
          '—'
        ) : r.result.error ? (
          answerTag(r)
        ) : (
          <Tag color="green">{r.result.status || 'ok'}</Tag>
        ),
    },
    {
      title: t('apiIntake.colInPlanner'),
      key: 'presence',
      render: (_, r) =>
        r.presence ? (
          <span>
            <Link to={`/admin/orders/${r.presence.orderCode}`}>{r.presence.orderCode}</Link>
            {r.presence.trashed && (
              <Tag color="orange" style={{ marginLeft: 6 }}>
                {t('apiIntake.inTrash')}
              </Tag>
            )}
          </span>
        ) : (
          <Tag color="red" icon={<ExclamationCircleOutlined />} data-testid="intake-order-missing">
            {t('apiIntake.missingInPlanner')}
          </Tag>
        ),
    },
    {
      title: t('apiIntake.colPlanner'),
      key: 'outcome',
      render: (_, r) =>
        !r.outcome ? (
          <span style={{ color: 'var(--text-muted)' }}>{t('apiIntake.noVerdict')}</span>
        ) : (
          <span>
            <Tag color={VERDICT_COLOR[r.outcome.verdict]}>{r.outcome.verdict}</Tag>
            {r.outcome.orderCode && <Link to={`/admin/orders/${r.outcome.orderCode}`}>{r.outcome.orderCode}</Link>}
            {r.outcome.reason && <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.outcome.reason}</div>}
          </span>
        ),
    },
  ];
  return (
    <Table
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={rows}
      rowClassName={(r) => (!r.presence && (r.result?.error || r.outcome) ? 'intake-row-missing' : '')}
      pagination={rows.length > 50 ? { pageSize: 50 } : false}
      style={{ marginBottom: 12 }}
      data-testid="intake-orders-table"
    />
  );
};

/**
 * Monitoring → API intake (Aug 2026). The exact raw JSON each partner POSTed
 * to ovo-api's /v2/partner-orders, forwarded to the planner as a 30-day
 * debugging ledger. A row can be REPLAYED against ovo-api — optionally as a
 * different partner code: resending a BUT body as BUT_TEST routes the batch
 * to the DEV planner (ovo-api's `_TEST` routing), which is how a partner's
 * tour mapping is rehearsed end-to-end before flipping direct-send on.
 */
const ApiIntakePage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  // OVO-292 — `?view=refusals` (house URL pattern): the open-refusals queue
  // the menu badge points at; `?partnerCode=` pre-filters the ledger.
  const view = searchParams.get('view') === 'refusals' ? 'refusals' : 'intakes';
  // Sep 2026 — `?errors=1`: only the requests with a serious error (never
  // mere "already exists" retries for orders the planner has).
  const onlyErrors = searchParams.get('errors') === '1';
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize('api-intake-columns', 50));
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(() =>
    searchParams.get('partnerCode') ? { partnerCode: searchParams.get('partnerCode') } : {},
  );
  // Lazily-fetched full rows (body + results + outcomes), keyed by row id.
  const [bodies, setBodies] = useState({});
  const [refusals, setRefusals] = useState({ items: [], total: 0, days: 14 });
  const [refusalsLoading, setRefusalsLoading] = useState(false);
  // Resend modal state.
  const [resendTarget, setResendTarget] = useState(null);
  const [asPartnerCode, setAsPartnerCode] = useState('');
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(
    async (p = page, ps = pageSize, f = filters, errorsOnly = onlyErrors) => {
      setLoading(true);
      try {
        const { response } = await getIntakes({
          take: ps,
          skip: (p - 1) * ps,
          ...f,
          ...(errorsOnly ? { onlyErrors: true } : {}),
        });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, filters, onlyErrors],
  );

  const loadRefusals = useCallback(async () => {
    setRefusalsLoading(true);
    try {
      const { response } = await getIntakeRefusals({ days: 30, take: 200 });
      setRefusals(response || { items: [], total: 0, days: 30 });
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setRefusalsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load(1, pageSize);
    loadRefusals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'refusals') next.set('view', 'refusals');
    else next.delete('view');
    setSearchParams(next, { replace: true });
    if (v === 'refusals') loadRefusals();
  };

  const setOnlyErrors = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('errors', '1');
    else next.delete('errors');
    setSearchParams(next, { replace: true });
    setPage(1);
    load(1, pageSize, filters, value);
  };

  const fetchBody = async (rec) => {
    if (bodies[rec.id] !== undefined) return;
    setBodies((b) => ({ ...b, [rec.id]: null })); // null = loading
    try {
      const { response } = await getIntake(rec.id);
      setBodies((b) => ({ ...b, [rec.id]: response ?? {} }));
    } catch (e) {
      setBodies((b) => ({ ...b, [rec.id]: { error: e?.message || 'load failed' } }));
    }
  };

  const openResend = (rec) => {
    setResendTarget(rec);
    setAsPartnerCode(rec.partnerCode || '');
    setResendResult(null);
  };

  const handleResend = async () => {
    if (!resendTarget) return;
    setResending(true);
    setResendResult(null);
    try {
      const override = asPartnerCode.trim();
      const { response } = await resendIntake(
        resendTarget.id,
        override && override !== resendTarget.partnerCode ? override : undefined,
      );
      setResendResult(response);
      if (response?.status >= 200 && response.status < 300) {
        messageApi.success(t('apiIntake.resendOk', { status: response.status }));
        // The resend may have closed refusals and changed row summaries.
        loadRefusals();
        load(page, pageSize);
      } else {
        messageApi.warning(t('apiIntake.resendStatus', { status: response?.status ?? '—' }));
      }
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setResending(false);
    }
  };

  const columns = [
    { title: t('apiIntake.time'), dataIndex: 'createdAt', key: 'createdAt', width: 175, render: fmtTime },
    {
      title: t('apiIntake.partner'),
      key: 'partner',
      width: 220,
      render: (_, rec) => (
        <span>
          <Tag color="blue">{rec.partnerCode}</Tag>
          {rec.partner?.name && <span style={{ color: 'var(--text-muted)' }}>{rec.partner.name}</span>}
        </span>
      ),
    },
    {
      title: t('apiIntake.orders'),
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 90,
      render: (v) => <b>{v}</b>,
    },
    {
      title: t('apiIntake.source'),
      dataIndex: 'source',
      key: 'source',
      width: 140,
      render: (v) => v || '—',
    },
    {
      // OVO-292 — the planner's verdicts for this request.
      title: t('apiIntake.outcome'),
      key: 'outcome',
      width: 260,
      render: (_, rec) => <OutcomeSummary summary={rec.outcomeSummary} t={t} />,
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, rec) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="link" size="small" icon={<SendOutlined />} onClick={() => openResend(rec)}>
            {t('apiIntake.resend')}
          </Button>
        </div>
      ),
    },
  ];

  const expanded = (rec) => {
    const detail = bodies[rec.id];
    if (detail === undefined || detail === null) return <Spin size="small" />;
    return (
      <div>
        <b>{t('apiIntake.ordersInRequest')}</b>
        <IntakeOrdersTable detail={detail} partnerCode={rec.partnerCode} t={t} />
        <b>{t('apiIntake.body')}</b>
        <Paragraph code copyable style={{ whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto', fontSize: 12 }}>
          {JSON.stringify(detail.body ?? detail, null, 2)}
        </Paragraph>
      </div>
    );
  };

  // OVO-292 — the open-refusals queue.
  const refusalColumns = [
    { title: t('apiIntake.time'), dataIndex: 'createdAt', key: 'createdAt', width: 175, render: fmtTime },
    {
      title: t('apiIntake.partner'),
      key: 'partner',
      width: 110,
      render: (_, r) => (r.partnerCode ? <Tag color="blue">{r.partnerCode}</Tag> : '—'),
    },
    {
      title: t('apiIntake.colPartnerRef'),
      key: 'ref',
      render: (_, r) => {
        const id = r.partnerOrderRef || r.orderRef;
        return id ? (
          <Link to={traceUrl(id, r.intakePartnerCode || r.partnerCode)}>
            <b>{id}</b>
          </Link>
        ) : (
          '—'
        );
      },
    },
    { title: t('apiIntake.colName'), dataIndex: 'name', key: 'name', render: (v) => v || '—' },
    { title: t('apiIntake.colDate'), dataIndex: 'deliveryDate', key: 'deliveryDate', width: 110, render: fmtDate },
    {
      title: t('apiIntake.colPlanner'),
      key: 'verdict',
      render: (_, r) => (
        <span>
          <Tag color={VERDICT_COLOR[r.verdict]}>
            {r.source === 'ovo-api' ? t('apiIntake.verdictOvoApi') : r.verdict}
          </Tag>
          {r.attempts > 1 && <Tag style={{ marginLeft: 4 }}>{t('apiIntake.attempts', { count: r.attempts })}</Tag>}
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.reason}</div>
        </span>
      ),
    },
    { title: t('apiIntake.colBy'), dataIndex: 'userEmail', key: 'userEmail', width: 200, render: (v) => v || '—' },
    {
      title: '',
      key: 'actions',
      width: 190,
      render: (_, r) => {
        const id = r.partnerOrderRef || r.orderRef;
        const partnerCode = r.intakePartnerCode || r.partnerCode;
        return (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {id && (
              <Link to={traceUrl(id, partnerCode)}>
                <Button type="link" size="small" icon={<SearchOutlined />}>
                  {t('apiIntake.trace')}
                </Button>
              </Link>
            )}
            {/* The intake row that carried this order — resend creates what
                is missing and skips what already exists. */}
            {r.intakeId && (
              <Button
                type="link"
                size="small"
                icon={<SendOutlined />}
                data-testid={`refusal-resend-${r.intakeId}`}
                onClick={() => openResend({ id: r.intakeId, partnerCode, orderCount: r.intakeOrderCount ?? '?' })}
              >
                {t('apiIntake.resend')}
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  const FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('api-intake-columns', columns, FIXED_KEYS);
  const overrideActive = resendTarget && asPartnerCode.trim() && asPartnerCode.trim() !== resendTarget.partnerCode;

  return (
    <div className="ApiIntakePage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <CloudDownloadOutlined style={{ color: 'var(--accent-strong)', marginRight: 8 }} />
              {t('apiIntake.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 720, marginTop: 4 }}>{t('apiIntake.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { value: 'intakes', label: t('apiIntake.viewIntakes'), icon: <CloudDownloadOutlined /> },
                {
                  value: 'refusals',
                  label: (
                    <span>
                      {t('apiIntake.viewRefusals')}
                      {refusals.total > 0 && (
                        <Badge count={refusals.total} size="small" overflowCount={99} style={{ marginLeft: 6 }} />
                      )}
                    </span>
                  ),
                  icon: <StopOutlined />,
                },
              ]}
              data-testid="api-intake-view"
            />
            {view === 'intakes' && (
              <Segmented
                value={onlyErrors ? 'errors' : 'all'}
                onChange={(v) => setOnlyErrors(v === 'errors')}
                options={[
                  { value: 'all', label: t('apiIntake.filterAll') },
                  { value: 'errors', label: t('apiIntake.filterErrors'), icon: <ExclamationCircleOutlined /> },
                ]}
                data-testid="api-intake-errors-filter"
              />
            )}
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading || refusalsLoading}
              onClick={() => (view === 'refusals' ? loadRefusals() : load(page, pageSize))}
            >
              {t('apiIntake.refresh')}
            </Button>
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        {view === 'refusals' && (
          <Spin spinning={refusalsLoading}>
            <Alert
              type={refusals.total > 0 ? 'warning' : 'success'}
              showIcon
              style={{ marginBottom: 12 }}
              message={
                refusals.total > 0
                  ? t('apiIntake.refusalsBanner', { count: refusals.total, days: refusals.days })
                  : t('apiIntake.refusalsNone', { days: refusals.days })
              }
              description={refusals.total > 0 ? t('apiIntake.refusalsHint') : undefined}
            />
            {refusals.items.length > 0 && (
              <Table
                rowKey="id"
                size="small"
                columns={refusalColumns}
                dataSource={refusals.items}
                pagination={refusals.items.length > 50 ? { pageSize: 50 } : false}
                scroll={{ x: 'max-content' }}
                data-testid="api-intake-refusals"
              />
            )}
          </Spin>
        )}
        {view === 'intakes' && (
          <Spin spinning={loading}>
            {rows.length === 0 && !loading ? (
              <Alert
                type={onlyErrors ? 'success' : 'info'}
                showIcon
                message={onlyErrors ? t('apiIntake.emptyErrors') : t('apiIntake.empty')}
              />
            ) : (
              <Table
                rowKey="id"
                size="small"
                columns={columnConfig.visibleColumns}
                dataSource={rows}
                rowClassName={intakeRowClass}
                expandable={{ expandedRowRender: expanded, onExpand: (open, rec) => open && fetchBody(rec) }}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  showSizeChanger: false,
                  onChange: (p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                    load(p, ps);
                  },
                }}
              />
            )}
          </Spin>
        )}

        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={(f) => {
            setFilters(f);
            setPage(1);
            load(1, pageSize, f);
          }}
          fields={[{ name: 'partnerCode', label: t('apiIntake.partner'), type: 'text', placeholder: 'BUT' }]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={(size) => {
            columnConfig.setPageSize(size);
            setPageSize(size);
            setPage(1);
            load(1, size);
          }}
        />

        <Modal
          title={t('apiIntake.resendTitle', { id: resendTarget?.id })}
          open={!!resendTarget}
          onCancel={() => setResendTarget(null)}
          onOk={handleResend}
          okText={t('apiIntake.resend')}
          okButtonProps={{ loading: resending, icon: <SendOutlined /> }}
          cancelText={t('common:cancel')}
          width={560}
        >
          {resendTarget && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                {t('apiIntake.resendSummary', {
                  count: resendTarget.orderCount,
                  code: resendTarget.partnerCode,
                })}
              </div>
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>{t('apiIntake.asPartnerCode')}</div>
                <Input
                  value={asPartnerCode}
                  onChange={(e) => setAsPartnerCode(e.target.value.toUpperCase())}
                  placeholder={resendTarget.partnerCode}
                />
              </div>
              <Alert
                type={overrideActive ? 'warning' : 'info'}
                showIcon
                message={overrideActive ? t('apiIntake.overrideHintActive') : t('apiIntake.overrideHint')}
              />
              {resendResult && (
                <Alert
                  type={resendResult.status >= 200 && resendResult.status < 300 ? 'success' : 'error'}
                  showIcon
                  message={t('apiIntake.resendStatus', { status: resendResult.status })}
                  description={
                    <Paragraph
                      code
                      style={{
                        whiteSpace: 'pre-wrap',
                        maxHeight: 180,
                        overflow: 'auto',
                        fontSize: 12,
                        marginBottom: 0,
                      }}
                    >
                      {typeof resendResult.response === 'string'
                        ? resendResult.response
                        : JSON.stringify(resendResult.response, null, 2)}
                    </Paragraph>
                  }
                />
              )}
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default ApiIntakePage;
